import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { I18nProvider } from "@/i18n/I18nContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/toaster";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AdminRoute from "@/components/layout/AdminRoute";
import PushNotificationPrompt from "@/components/layout/PushNotificationPrompt";
import SocialProofPopup from "@/components/layout/SocialProofPopup";
import ScrollToTop from "@/components/layout/ScrollToTop";
import { usePageViewTracking } from "@/hooks/useAnalytics";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import Wizard from "@/pages/Wizard";
import Checkout from "@/pages/Checkout";
import PaymentReturn from "@/pages/PaymentReturn";
import OrderStatus from "@/pages/OrderStatus";
import Dashboard from "@/pages/Dashboard";
import MyCreations from "@/pages/MyCreations";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import SalesTerms from "@/pages/SalesTerms";
import CookiePolicy from "@/pages/CookiePolicy";
import Contact from "@/pages/Contact";
import Refunds from "@/pages/Refunds";
import PublicSong from "@/pages/PublicSong";
import Services from "@/pages/Services";
import CreerChansonPersonnalisee from "@/pages/CreerChansonPersonnalisee";
import CadeauMusicalPersonnalise from "@/pages/CadeauMusicalPersonnalise";
import VotreHistoireEnMusique from "@/pages/VotreHistoireEnMusique";
import FAQPage from "@/pages/FAQPage";
import ReviewsPage from "@/pages/ReviewsPage";
import OccasionLanding from "@/pages/OccasionLanding";
import GeneratorLanding from "@/pages/GeneratorLanding";
import Gallery from "@/pages/Gallery";
import VoiceMessageLanding from "@/pages/VoiceMessageLanding";
import BlogList from "@/pages/BlogList";
import BlogArticle from "@/pages/BlogArticle";

function AppRoutes() {
  usePageViewTracking();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/mentions-legales" element={<Legal />} />
      <Route path="/confidentialite" element={<PrivacyPolicy />} />
      <Route path="/cgu" element={<TermsOfUse />} />
      <Route path="/cgv" element={<SalesTerms />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/services" element={<Services />} />
      <Route path="/creer-chanson-personnalisee" element={<CreerChansonPersonnalisee />} />
      <Route path="/cadeau-musical-personnalise" element={<CadeauMusicalPersonnalise />} />
      <Route path="/votre-histoire-en-musique" element={<VotreHistoireEnMusique />} />
      <Route path="/avis" element={<ReviewsPage />} />
      <Route path="/galerie" element={<Gallery />} />
      <Route path="/musique-mariage" element={<OccasionLanding slug="mariage" />} />
      <Route path="/musique-anniversaire" element={<OccasionLanding slug="anniversaire" />} />
      <Route path="/musique-naissance" element={<OccasionLanding slug="naissance" />} />
      <Route path="/idee-cadeau-original" element={<OccasionLanding slug="cadeau" />} />
      <Route path="/chanson-enterrement-hommage" element={<OccasionLanding slug="enterrement" />} />
      <Route path="/chanson-depart-retraite" element={<OccasionLanding slug="retraite" />} />
      <Route path="/chanson-fiancailles" element={<OccasionLanding slug="fiancailles" />} />
      <Route path="/chanson-evjf-evg" element={<OccasionLanding slug="evjf_evg" />} />
      <Route path="/chanson-remise-diplome" element={<OccasionLanding slug="diplome" />} />
      <Route path="/chanson-baby-shower" element={<OccasionLanding slug="babyshower" />} />
      <Route path="/chanson-fete-des-meres" element={<OccasionLanding slug="fete_des_meres" />} />
      <Route path="/chanson-fete-des-peres" element={<OccasionLanding slug="fete_des_peres" />} />
      <Route path="/chanson-saint-valentin" element={<OccasionLanding slug="saint_valentin" />} />
      <Route path="/chanson-cadeau-noel" element={<OccasionLanding slug="noel" />} />
      <Route path="/chanson-anniversaire-mariage" element={<OccasionLanding slug="anniversaire_mariage" />} />
      <Route path="/chanson-pot-de-depart-collegue" element={<OccasionLanding slug="pot_depart" />} />
      <Route path="/chanson-remerciement" element={<OccasionLanding slug="remerciement" />} />
      <Route path="/chanson-bapteme" element={<OccasionLanding slug="bapteme" />} />
      <Route path="/chanson-cadeau-amitie" element={<OccasionLanding slug="amitie" />} />
      <Route path="/message-vocal-personnalise" element={<VoiceMessageLanding />} />
      <Route path="/fr/generateur-musique-ia" element={<GeneratorLanding slug="musique_ia" locale="fr" />} />
      <Route path="/en/ai-music-generator" element={<GeneratorLanding slug="musique_ia" locale="en" />} />
      <Route path="/fr/creer-chanson-ia" element={<GeneratorLanding slug="creer_chanson_ia" locale="fr" />} />
      <Route path="/en/ai-song-generator" element={<GeneratorLanding slug="creer_chanson_ia" locale="en" />} />
      <Route path="/fr/generateur-voix-chantee-ia" element={<GeneratorLanding slug="voix_chantee_ia" locale="fr" />} />
      <Route path="/en/ai-singing-voice-generator" element={<GeneratorLanding slug="voix_chantee_ia" locale="en" />} />
      <Route path="/fr/generateur-rap-ia" element={<GeneratorLanding slug="rap_ia" locale="fr" />} />
      <Route path="/en/ai-rap-generator" element={<GeneratorLanding slug="rap_ia" locale="en" />} />
      <Route path="/fr/generateur-pop-ia" element={<GeneratorLanding slug="pop_ia" locale="fr" />} />
      <Route path="/en/ai-pop-song-generator" element={<GeneratorLanding slug="pop_ia" locale="en" />} />
      <Route path="/fr/generateur-rock-ia" element={<GeneratorLanding slug="rock_ia" locale="fr" />} />
      <Route path="/en/ai-rock-song-generator" element={<GeneratorLanding slug="rock_ia" locale="en" />} />
      <Route path="/fr/generateur-country-ia" element={<GeneratorLanding slug="country_ia" locale="fr" />} />
      <Route path="/en/ai-country-music-generator" element={<GeneratorLanding slug="country_ia" locale="en" />} />
      <Route path="/fr/generateur-metal-ia" element={<GeneratorLanding slug="metal_ia" locale="fr" />} />
      <Route path="/en/ai-metal-generator" element={<GeneratorLanding slug="metal_ia" locale="en" />} />
      <Route path="/fr/generateur-electro-ia" element={<GeneratorLanding slug="electro_ia" locale="fr" />} />
      <Route path="/en/ai-edm-generator" element={<GeneratorLanding slug="electro_ia" locale="en" />} />
      <Route path="/fr/generateur-jazz-ia" element={<GeneratorLanding slug="jazz_ia" locale="fr" />} />
      <Route path="/en/ai-jazz-generator" element={<GeneratorLanding slug="jazz_ia" locale="en" />} />
      <Route path="/fr/generateur-reggae-ia" element={<GeneratorLanding slug="reggae_ia" locale="fr" />} />
      <Route path="/fr/generateur-blues-ia" element={<GeneratorLanding slug="blues_ia" locale="fr" />} />
      <Route path="/en/ai-blues-generator" element={<GeneratorLanding slug="blues_ia" locale="en" />} />
      <Route path="/fr/generateur-lofi-ia" element={<GeneratorLanding slug="lofi_ia" locale="fr" />} />
      <Route path="/en/ai-lofi-generator" element={<GeneratorLanding slug="lofi_ia" locale="en" />} />
      <Route path="/fr/generateur-musique-classique-ia" element={<GeneratorLanding slug="classique_ia" locale="fr" />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/s/:token" element={<PublicSong />} />
      <Route path="/create" element={<Wizard />} />
      <Route path="/checkout/:orderId" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
      <Route path="/payments/return" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute><OrderStatus /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/mes-creations" element={<ProtectedRoute><MyCreations /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("ams_referral_code", ref);
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <I18nProvider>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppRoutes />
              <SocialProofPopup />
            </BrowserRouter>
            <Toaster />
            <PushNotificationPrompt />
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
